@use "Styles/includes/variables";

:host {
    display: contents;
}

// Allow the floating medallion to overflow the dialog's top edge.
vm-modal {
    --vm-modal-overflow: visible;
}

// ── Mobile modal (vm-modal wrapper) ────────────────────────────────────────

.vpm {
    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 8px 0 0;
    }

    &__medallion {
        width: 64px;
        height: 64px;
        border-radius: 14px;
        background: #ffffff;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        display: grid;
        place-items: center;
        // Positioned relative to the <dialog> (which has position: absolute in the UA stylesheet),
        // so __body's overflow-y: auto does not clip it.
        position: absolute;
        top: -32px;
        left: 50%;
        transform: translate(-50%);
    }

    &__headline {
        margin: 0 0 8px;
        font-family: variables.$fontQuaternary;
        font-size: 22px;
        font-weight: 700;
        line-height: 1.2;
        color: #181d27;
        text-wrap: balance;
    }

    &__subhead {
        margin: 0 0 24px;
        font-size: 15px;
        line-height: 1.4;
        color: #535862;
        text-wrap: balance;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        width: 100%;
    }

    &__cta {
        width: 100%;
    }
}

// ── Desktop bottom-left panel ───────────────────────────────────────────────

.vpm__panel {
    position: fixed;
    bottom: 24px;
    left: 24px;
    z-index: 100;
    width: 360px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 18px;
    box-shadow:
        0 20px 50px rgba(0, 0, 0, 0.1),
        0 4px 12px rgba(0, 0, 0, 0.06);
    padding: 20px;
    opacity: 0;
    transform: translateY(12px) scale(0.98);
    transition:
        opacity 0.28s cubic-bezier(0.16, 1, 0.3, 1),
        transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: none;

    &--open {
        opacity: 1;
        transform: none;
        pointer-events: auto;
    }

    &-close {
        position: absolute;
        top: 12px;
        right: 12px;

        // Override vm-button-next sm (36×36) to match design (26×26) and exact colors.
        // !important is required because internal :host([hierarchy="quaternary"]) button
        // rules have higher specificity than external ::part(button).
        &::part(button) {
            width: 26px !important;
            height: 26px !important;
            border-radius: 50% !important;
            color: #8e8e93 !important;
            background-color: transparent !important;
        }

        &:hover::part(button) {
            background-color: rgba(0, 0, 0, 0.05) !important;
            color: #1d1d1f !important;
        }
    }

    &-header {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        margin-bottom: 16px;
        padding-right: 20px; // room for close button
    }

    &-heading {
        flex: 1;
        min-width: 0;
    }

    &-app-name {
        font-size: 15px;
        font-weight: 600;
        margin: 0;
        letter-spacing: -0.01em;
        color: #1d1d1f;
    }

    &-tagline {
        font-size: 12px;
        color: #6e6e73;
        margin: 0;
    }

    &-rating {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #6e6e73;
    }

    &-stars {
        display: inline-flex;
        gap: 1px;
        color: #ff9500;
    }

    &-rating-value {
        font-weight: 500;
        color: #1d1d1f;
    }

    &-body {
        margin-bottom: 16px;
    }

    &-headline {
        font-size: 17px;
        font-weight: 600;
        letter-spacing: -0.015em;
        margin: 0 0 4px;
        color: #1d1d1f;
    }

    &-value-prop {
        font-size: 13px;
        line-height: 1.45;
        color: #6e6e73;
        margin: 0;
    }

    &-qr-row {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px;
        background: #fafafa;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 12px;
        margin-bottom: 12px;
    }

    &-qr {
        width: 92px;
        height: 92px;
        background: white;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        padding: 6px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.15s ease;

        img {
            width: 100%;
            height: 100%;
            display: block;
        }

        &:is(a):hover {
            opacity: 0.8;
        }
    }

    &-qr-text {
        flex: 1;
        min-width: 0;
    }

    &-qr-label {
        font-size: 13px;
        font-weight: 500;
        color: #1d1d1f;
        margin: 0 0 4px;
        letter-spacing: -0.005em;
    }

    &-qr-sub {
        font-size: 11.5px;
        line-height: 1.4;
        color: #8e8e93;
        margin: 0;
    }

    &-divider {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 12px;
        font-size: 11px;
        color: #8e8e93;
        text-transform: uppercase;
        letter-spacing: 0.06em;

        &::before,
        &::after {
            content: "";
            flex: 1;
            height: 1px;
            background: rgba(0, 0, 0, 0.08);
        }
    }

    &-badge-row {
        display: flex;
        justify-content: center;
    }

    &-badge {
        display: inline-block;
        text-decoration: none;
        line-height: 0;
        transition:
            opacity 0.15s ease,
            transform 0.15s ease;

        img {
            height: 40px;
            width: auto;
            display: block;
        }

        &:hover {
            opacity: 0.85;
        }

        &:active {
            transform: scale(0.98);
        }
    }

    &-badge-text {
        font-size: 14px;
        font-weight: 500;
        color: #1d1d1f;
        text-decoration: underline;
        text-underline-offset: 2px;
    }
}

// ── App icon (squircle with drop-shadow, matches designer spec) ────────────

.vpm__app-icon {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    background: white;
    border-radius: 22%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.08)) drop-shadow(0 3px 6px rgba(0, 0, 0, 0.1))
        drop-shadow(0 0 0.5px rgba(0, 0, 0, 0.2));

    img {
        display: block;
    }
}
